/**
 * @module Service
 */

import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Page, PageRequest } from '@app/models/page.model';
import {
	AFTRA_ORDER_TEMPLATE_ID,
	Order,
	OrderBulkAction,
	OrderBulkActionInput,
	OrderBulkActionOutput,
	OrderBulkImportInput,
	OrderBulkImportOutput,
	OrderCount,
	OrderLineItem,
	OrderQueue,
	OrderQueueDocumentInput,
	OrderQueueType, OrderStatus,
} from '@feature/orders/shared';
import { BaseHttpService } from '@app/services/base-http.service';
import { ACX_SIGNATORY_ID, Profile } from '@feature/profiles/shared';
import { OrderUserPair } from '@feature/users/shared';
import { PAYER_SIGNATORY_OPTIONS } from '@app/components/custom-fields/shared/custom-field.model';

/**
 * Service for managing order-related operations within the application.
 *
 * This service extends the BaseHttpService to provide additional functionality
 * specific to order management, such as fetching, creating or updating order data,
 * and handling report generation. It uses Angular HttpClient for making HTTP requests
 * to the backend API. It is provided at the root level, making it available
 * throughout the application.
 *
 * @service
 * @extends {BaseHttpService}
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class OrderService extends BaseHttpService {
	/**
	 * Options for HTTP requests, including headers.
	 * @type {{ headers: HttpHeaders }}
	 * @note Used for the CRUD operations on the user.
	 */
	options: { headers: HttpHeaders } = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	/**
	 * Creates an instance of the OrderService.
	 *
	 * @constructor
	 * @param {HttpClient} _http - The HttpClient used for making HTTP requests.
	 */
	constructor(private _http: HttpClient) {
		super(_http);
	}

	/**
	 * Retrieves a paginated list of orders associated with a specific payer.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, including
	 * an optional flag to check for pending orders. It sends a GET request to retrieve the orders
	 * from the API and returns an observable containing the paginated list of orders.
	 *
	 * @param {number} payerId - The unique identifier of the payer whose orders are to be retrieved.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<Page<Order>>} An observable containing the paginated list of orders associated with the payer.
	 */
	public getPayerOrders(
		payerId: number,
		pageable?: PageRequest,
		search?: string,
		checkPending: boolean = true
	): Observable<Page<Order>> {
		let parameters: HttpParams = this.params(pageable, search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this._http.get<Page<Order>>(`${environment.config.apiUrl}/payers/${payerId}/orders`, { params: parameters });
	}

	/**
	 * Count all orders based on the search criteria associated with a specific payer.
	 *
	 * @param {number} payerId - The unique identifier of the payer whose orders are to be retrieved.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<OrderCount>}
	 */
	public getPayerOrdersCount(
		payerId: number,
		search?: string,
		checkPending: boolean = true
	): Observable<OrderCount> {
		let parameters: HttpParams = new HttpParams();
		parameters = parameters.append('search', search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this.http.get<OrderCount>(`${environment.config.apiUrl}/payers/${payerId}/orders/count`, { params: parameters });
	}

	/**
	 * Retrieves a paginated list of orders associated with a specific payee.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, including
	 * an optional flag to check for pending orders. It sends a GET request to retrieve the orders
	 * from the API and returns an observable containing the paginated list of orders.
	 *
	 * @param {number} payeeId - The unique identifier of the payee whose orders are to be retrieved.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<Page<Order>>} An observable containing the paginated list of orders associated with the payee.
	 */
	public getPayeeOrders(
		payeeId: number,
		pageable?: PageRequest,
		search?: string,
		checkPending: boolean = true
	): Observable<Page<Order>> {
		let parameters: HttpParams = this.params(pageable, search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this._http.get<Page<Order>>(`${environment.config.apiUrl}/payees/${payeeId}/orders`, { params: parameters });
	}

	/**
	 * Count all orders based on the search criteria associated with a specific payee.
	 *
	 * @param {number} payeeId - The unique identifier of the payee whose orders are to be retrieved.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<OrderCount>}
	 */
	public getPayeeOrdersCount(
		payeeId: number,
		search?: string,
		checkPending: boolean = true
	): Observable<OrderCount> {
		let parameters: HttpParams = new HttpParams();
		parameters = parameters.append('search', search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this.http.get<OrderCount>(`${environment.config.apiUrl}/payees/${payeeId}/orders/count`, { params: parameters });
	}

	/**
	 * Retrieves a paginated list of orders associated with a specific company.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, including
	 * an optional flag to check for pending orders. It sends a GET request to retrieve the orders
	 * from the API and returns an observable containing the paginated list of orders.
	 *
	 * @param {number} companyId - The unique identifier of the company whose orders are to be retrieved.
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<Page<Order>>} An observable containing the paginated list of orders associated with the payee.
	 */
	public getCompanyOrders(
		companyId: number,
		pageable?: PageRequest,
		search?: string,
		checkPending: boolean = true
	): Observable<Page<Order>> {
		let parameters: HttpParams = this.params(pageable, search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this._http.get<Page<Order>>(`${environment.config.apiUrl}/companies/${companyId}/orders`, { params: parameters });
	}

	/**
	 * Count all orders based on the search criteria associated with a specific company.
	 *
	 * @param {number} companyId - The unique identifier of the company whose orders are to be retrieved.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<OrderCount>}
	 */
	public getCompanyOrdersCount(
		companyId: number,
		search?: string,
		checkPending: boolean = true
	): Observable<OrderCount> {
		let parameters: HttpParams = new HttpParams();
		parameters = parameters.append('search', search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this.http.get<OrderCount>(`${environment.config.apiUrl}/companies/${companyId}/orders/count`, { params: parameters });
	}

	/**
	 * Retrieves a paginated list of orders, optionally filtered by search term and pending status.
	 *
	 * This method constructs the necessary HTTP parameters for pagination and search, including
	 * an optional flag to check for pending orders. It sends a GET request to retrieve the orders
	 * from the API and returns an observable containing the paginated list of orders.
	 *
	 * @param {PageRequest} [pageable] - Optional pagination parameters.
	 * @param {string} [search] - Optional search term to filter the orders.
	 * @param {boolean} [checkPending=true] - Optional flag indicating whether to check for pending orders.
	 * @returns {Observable<Page<Order>>} An observable containing the paginated list of orders.
	 */
	public getOrders(pageable?: PageRequest, search?: string, checkPending: boolean = true): Observable<Page<Order>> {
		let parameters: HttpParams = this.params(pageable, search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this._http.get<Page<Order>>(`${environment.config.apiUrl}/orders`, { params: parameters });
	}

	/**
	 * Count all orders based on the search criteria
	 *
	 * @param {string} search
	 * @param {boolean} checkPending
	 * @returns {Observable<OrderCount>}
	 */
	public getOrdersCount(search?: string, checkPending: boolean = true): Observable<OrderCount> {
		let parameters: HttpParams = new HttpParams();
		parameters = parameters.append('search', search);
		parameters = parameters.append('checkPending', (checkPending ? 'true' : 'false'));
		return this.http.get<OrderCount>(`${environment.config.apiUrl}/orders/count`, { params: parameters });
	}

	/**
	 * Retrieves a specific order by its unique identifier.
	 *
	 * This method sends a GET request to fetch the details of a specific order based on the provided
	 * order ID. It returns an observable containing the requested order object.
	 *
	 * @param {number} orderId - The unique identifier of the order to retrieve.
	 * @returns {Observable<Order>} An observable containing the requested order object.
	 */
	public getOrder(orderId: number): Observable<Order> {
		return this._http.get<Order>(`${environment.config.apiUrl}/orders/${orderId}`, this.options);
	}

	/**
	 * Creates a new order based on the provided order data.
	 *
	 * This method sends a POST request to create a new order using the specified order object.
	 * It returns an observable containing the created order object.
	 *
	 * @param {Order} order - The order object containing the details of the order to create.
	 * @returns {Observable<Order>} An observable containing the created order object.
	 */
	public createOrder(order: Order): Observable<Order> {
		return this._http.post<Order>(`${environment.config.apiUrl}/orders`, order, this.options);
	}

	/**
	 * Updates an order based on the provided order data.
	 *
	 * This method sends a PUT request to update the existing order using the specified order object.
	 * It returns an observable containing the updated order object.
	 *
	 * @param {Order} order - The order object containing the details of the order to update.
	 * @returns {Observable<Order>} An observable containing the updated order object.
	 */
	public updateOrder(order: Order): Observable<Order> {
		return this._http.put<Order>(`${environment.config.apiUrl}/orders`, order, this.options);
	}

	/**
	 * Creates a single order queue entry.
	 *
	 * This method sends a POST request to create a new order queue using the specified order object.
	 * It returns an observable containing the created order queue.
	 *
	 * @param {Order} order - The order object containing the details of the order to create.
	 * @param {OrderQueueType} [queueType=OrderQueueType.normal] - Specifies if the queue is deferred, or normal by default.
	 * @returns {Observable<Order>} An observable containing the created order object.
	 */
	public createOrderQueue(order: Order, queueType: OrderQueueType = OrderQueueType.normal): Observable<OrderQueue> {
		let params: HttpParams = new HttpParams();
		params = params.append('type', queueType.toString());
		return this._http.post<OrderQueue>(`${environment.config.apiUrl}/orders/queue`, order, { params });
	}

	/**
	 * Performs a bulk action on orders based on the provided input.
	 *
	 * This method sends a PUT request to execute a bulk action on orders, using the specified input
	 * parameters. It returns an observable containing the result of the bulk action.
	 *
	 * @param {OrderBulkActionInput} input - The input object containing details of the bulk action to perform.
	 * @returns {Observable<OrderBulkActionOutput>} An observable containing the output of the bulk action.
	 */
	public bulkAction(input: OrderBulkActionInput): Observable<OrderBulkActionOutput> {
		return this._http.put<OrderBulkActionOutput>(`${environment.config.apiUrl}/orders/bulk-action`, input, this.options);
	}

	/**
	 * Initiates bulk orders creation based on the CSV document import.
	 *
	 * @param {OrderBulkImportInput} input - The input data for bulk orders import.
	 * @returns {Observable<OrderBulkImportOutput>} An observable of the bulk orders import output.
	 */
	public bulkOrdersImport(input: OrderBulkImportInput): Observable<OrderBulkImportOutput> {
		return this._http.put<OrderBulkImportOutput>(`${environment.config.apiUrl}/orders/import`, input, this.options);
	}

	/**
	 * Uploads the source document and updates the associated order queue.
	 *
	 * @param {OrderQueueDocumentInput} input - The input data for the document upload.
	 * @returns {Observable<OrderQueue>} An observable of the updated order queue.
	 */
	public orderQueueDocumentUpload(input: OrderQueueDocumentInput): Observable<OrderQueue> {
		return this._http.put<OrderQueue>(`${environment.config.apiUrl}/orders/import/document`, input, this.options);
	}

	/**
	 * Retrieves an order report for a specified user.
	 *
	 * This method sends a GET request to fetch a report associated with the given user ID. It includes
	 * an optional search parameter to filter the report results. The response is expected to be a Blob,
	 * which can be used for file downloads or binary data processing.
	 *
	 * @param {number} userId - The unique identifier of the user for whom the report is requested.
	 * @param {string} search - The search term to filter the report results.
	 * @returns {Observable<HttpResponse<Blob>>} An observable containing the HTTP response with the report as a Blob.
	 */
	public getReportPersonal(userId: number, search: string): Observable<HttpResponse<Blob>> {
		return this.http.get<Blob>(
			`${environment.config.apiUrl}/user/${userId}/report?search=${search}`,
			{ observe: 'response', responseType: 'blob' as 'json' }
		);
	}

	/**
	 * Retrieves an order report for a specified company.
	 *
	 * This method sends a GET request to fetch a report associated with the given company ID. It includes
	 * an optional search parameter to filter the report results. The response is expected to be a Blob,
	 * which can be used for file downloads or binary data processing.
	 *
	 * @param {number} companyId - The unique identifier of the company for which the report is requested.
	 * @param {string} search - The search term to filter the report results.
	 * @returns {Observable<HttpResponse<Blob>>} An observable containing the HTTP response with the report as a Blob.
	 */
	public getReportCompany(companyId: number, search: string): Observable<HttpResponse<Blob>> {
		return this.http.get<Blob>(
			`${environment.config.apiUrl}/company/${companyId}/report?search=${search}`,
			{ observe: 'response', responseType: 'blob' as 'json' }
		);
	}

	/**  */
	/**
	 * Constructs a user-friendly label for the order action.
	 *
	 * @param {OrderBulkAction} bulkAction
	 * @param {boolean} isAdmin
	 * @param {boolean} isPlural
	 * @returns {string}
	 */
	public getActionDescription(
		bulkAction: OrderBulkAction,
		isAdmin?: boolean,
		isPlural: boolean = true
	) : string {
		const isPluralFormat: string = isPlural ? '(-s)' : '';
		switch (bulkAction) {
			case OrderBulkAction.ReSendEmails:
				return `Send email invoice${isPluralFormat}/receipt${isPluralFormat}`;
			case OrderBulkAction.Cancel:
				if (isAdmin) return `Void/Cancel (QBO, if applicable)`;
				return `Void/Cancel`;
			case OrderBulkAction.PushToQuickbooks:
				return `Send to QBO`;
			case OrderBulkAction.ResetQBO:
				return `Reset QBO status in Eljin App`;
			case OrderBulkAction.ResetPayment:
				return `Reset payment status in Eljin App`;
			case OrderBulkAction.ResetCollection:
				return `Reset collection status in Eljin App`;
			case OrderBulkAction.DownloadPayment:
				return `Download`;
			case OrderBulkAction.ApprovePayment:
				return `Approve`;
			case OrderBulkAction.DeclinePayment:
				return `Decline`;
			default:
				return `Unknown bulk action`;
		}
	}

	/**
	 * Checks if the invoiced user has the ACX signatory.
	 *
	 * @param {Profile} payerProfile
	 * @param {OrderUserPair} currentPayee
	 * @returns {boolean}
	 */
	public isACX(payerProfile: Profile, currentPayee: OrderUserPair): boolean {
		if (payerProfile == null) return false;
		if (payerProfile && currentPayee?.defaultTemplate?.id === AFTRA_ORDER_TEMPLATE_ID) {
			const payerSignatory = payerProfile?.customFieldValues.find(cfValue => cfValue.fieldName === PAYER_SIGNATORY_OPTIONS);
			return payerSignatory && parseInt(payerSignatory?.values, 10) === ACX_SIGNATORY_ID;
		}
		return false;
	}

	/**
	 * Checks if the invoiced user has the Brilliance signatory.
	 *
	 * @param {string} companyName
	 * @returns {boolean}
	 */
	public isBrilliance(companyName: string): boolean {
		// @todo ETT-280 - temporary hack to have special handling of Brilliance - remove when we have customer preferences.
		if (companyName == null) { return false; }
		return companyName.toUpperCase().indexOf('brilliance'.toUpperCase()) !== -1;
	}

	/**
	 * Checks if the order is a draft and has to be completed.
	 *
	 * @param {Order} order
	 * @returns {boolean}
	 */
	public static isDraft(order: Order): boolean {
		if (order == null) return false;
		// @note Focus on a primary status check that defines the draft.
		return order?.statuses?.some((status: OrderStatus) => status?.statusAlias === 'queue_invalid');
	}

	/**
	 * Checks the flat rate calculation for the given order and displays a notice, if applicable.
	 *
	 * @returns {void}
	 * @param isFlatRateCalculation - The order flat rate calculation to be checked.
	 * @param flatRateValue - The quantity and rate calculation in case of the flat rate.
	 * @param lineItems - The order line items.
	 */
	public checkFlatRateRoundingThenShowNotice(
		isFlatRateCalculation: boolean,
		flatRateValue: number,
		lineItems: Array<OrderLineItem>
	): boolean {
		if (isFlatRateCalculation) {
			// We know talent is the first element on the line.
			for (const item of lineItems) {
				if (item.templateStartReverseCalculation) {
					if (item.value !== flatRateValue) {
						return true;
					}
				}
			}
		}
		return false;
	}

	/**
	 * Checks if the order has invoice or deferred numbers.
	 *
	 * @param {Order} order
	 * @returns {boolean | null}
	 */
	public static hasInvoiceOrDeferredNumbers(order: Order): null | boolean {
		if (order == null) return null;
		return order?.number != null && order?.deferredNumber?.length > 0;
	}
}
